<script setup lang="ts">
import InitialsAvatar from '@/components/general/InitialsAvatar.vue';
import useContactsService from '@/composables/apiServices/useContactsService';
import type {
  IContactInitialsAvatarEvents,
  IContactInitialsAvatarProps
} from '@/types/components/general/ContactInitialsAvatar';
import type { VIntersectCallback } from '@/types/utils/v-intersect';
import { computed, ref, watch } from 'vue';

const props = withDefaults(defineProps<IContactInitialsAvatarProps>(), { size: '40' });

const emit = defineEmits<IContactInitialsAvatarEvents>();

const isComponentVisible = ref(false);
const currentContactId = ref(props.contactId ?? null);
const shouldFetchAvatar = computed(() => (props.hasAvatar ?? false) && isComponentVisible.value);

const { getContactAvatar } = useContactsService();
const { data: avatarData } = getContactAvatar(currentContactId, shouldFetchAvatar);

const fetchedAvatarUrl = ref<string | null>(null);

const onIntersect: VIntersectCallback = (isIntersecting) => {
  // More information about these options
  // is located here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
  isComponentVisible.value = isIntersecting;
};

watch(
  () => avatarData.value,
  (newValue) => {
    if (newValue) {
      fetchedAvatarUrl.value = newValue;
      emit('fileLoaded', newValue);
    } else {
      fetchedAvatarUrl.value = null;
    }
  },
  { immediate: true }
);

const contactAvatar = computed(() => {
  if (props.image) {
    return props.image;
  } else if (fetchedAvatarUrl.value && props.hasAvatar) {
    return fetchedAvatarUrl.value;
  }
  return undefined;
});
</script>

<template>
  <InitialsAvatar
    v-intersect="onIntersect"
    :is-selected="isSelected"
    :image="contactAvatar"
    :name="name"
    :size="size"
    :color="color"
    :icon="icon"
  />
</template>
